import { ChevronDownIcon } from '@radix-ui/react-icons';
import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { cva } from 'class-variance-authority';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import type * as React from 'react';

import { cn } from '~/utils/cn';

import { transition } from './utils/animations';

function NavigationMenu({ className, children, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.Root>) {
	return (
		<NavigationMenuPrimitive.Root
			className={cn('relative z-10 flex max-w-max flex-1 items-center justify-center', className)}
			data-slot="navigation-menu"
			{...props}
		>
			{children}
			{/* <NavigationMenuViewport /> */}
		</NavigationMenuPrimitive.Root>
	);
}

function NavigationMenuList({ className, ...props }: React.ComponentProps<typeof NavigationMenuPrimitive.List>) {
	return (
		<NavigationMenuPrimitive.List
			className={cn('group flex list-none items-center justify-center gap-x-0', className)}
			data-slot="navigation-menu-list"
			{...props}
		/>
	);
}

const NavigationMenuItem = NavigationMenuPrimitive.Item;

const navigationMenuTriggerStyle = cva(
	// Lyra style: sharp corners, h-8, text-xs
	'group inline-flex h-8 w-max items-center justify-center rounded-none bg-transparent font-medium text-xs transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-hidden disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50 data-active:bg-accent/50',
);

function NavigationMenuTrigger({
	asChild,
	className,
	children,
	hasDropdownArrow = true,
	...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger> & { hasDropdownArrow?: boolean }) {
	return (
		<NavigationMenuPrimitive.Trigger
			className={cn(navigationMenuTriggerStyle(), 'group', className)}
			data-slot="navigation-menu-trigger"
			{...props}
		>
			{children}
			{hasDropdownArrow && (
				<ChevronDownIcon
					aria-hidden="true"
					className="relative top-[1px] ml-1 h-3 w-3 transition duration-300 group-data-[state=open]:rotate-180"
				/>
			)}
		</NavigationMenuPrimitive.Trigger>
	);
}

type NavigationMenuContentProps = React.ComponentProps<typeof NavigationMenuPrimitive.Content> & {
	transition?: 'accordion';
	active?: boolean;
};

const transitionVariants: Variants = {
	animate: { height: 'auto', opacity: 1, transition: transition.accordion },
	exit: { height: 0, opacity: 0, transition: transition.accordion },
	initial: { height: 0, opacity: 0, transition: transition.accordion },
};

function NavigationMenuContent({ className, transition, active, ...props }: NavigationMenuContentProps) {
	return (
		<NavigationMenuPrimitive.Content
			className={cn(
				'w-full md:w-auto',
				'group-data-[viewport=false]/navigation-menu:bg-popover',
				'group-data-[viewport=false]/navigation-menu:text-popover-foreground',
				'group-data-[viewport=false]/navigation-menu:mt-1.5',
				'group-data-[viewport=false]/navigation-menu:overflow-hidden',
				// Lyra style: sharp corners
				'group-data-[viewport=false]/navigation-menu:rounded-none',
				'group-data-[viewport=false]/navigation-menu:border',
				'**:data-[slot=navigation-menu-link]:focus:outline-none **:data-[slot=navigation-menu-link]:focus:ring-0 group-data-[viewport=false]/navigation-menu:duration-200',
				className,
			)}
			data-slot="navigation-menu-content"
			forceMount
			{...props}
		>
			<AnimatePresence>
				{active && (
					<motion.div animate="animate" className="w-full" exit="exit" initial="initial" variants={transitionVariants}>
						{props.children}
					</motion.div>
				)}
			</AnimatePresence>
		</NavigationMenuPrimitive.Content>
	);
}

const NavigationMenuLink = NavigationMenuPrimitive.Link;

function NavigationMenuViewport({
	className,
	...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) {
	return (
		<div className={cn('absolute top-full left-0 flex justify-center')}>
			<NavigationMenuPrimitive.Viewport
				className={cn(
					// Lyra style: sharp corners
					'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full origin-top-center overflow-hidden rounded-none border bg-popover text-popover-foreground shadow-sm data-[state=closed]:animate-out data-[state=open]:animate-in md:w-[var(--radix-navigation-menu-viewport-width)]',
					className,
				)}
				data-slot="navigation-menu-viewport"
				{...props}
			/>
		</div>
	);
}

function NavigationMenuIndicator({
	className,
	...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Indicator>) {
	return (
		<NavigationMenuPrimitive.Indicator
			className={cn(
				'data-[state=hidden]:fade-out data-[state=visible]:fade-in top-full z-1 flex h-1.5 items-end justify-center overflow-hidden data-[state=hidden]:animate-out data-[state=visible]:animate-in',
				className,
			)}
			data-slot="navigation-menu-indicator"
			{...props}
		>
			<div className="relative top-[60%] h-2 w-2 rotate-45 rounded-tl-sm bg-border shadow-md" />
		</NavigationMenuPrimitive.Indicator>
	);
}

export {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuIndicator,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	NavigationMenuViewport,
	navigationMenuTriggerStyle,
};
